import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import OnboardingModal from "./components/OnboardingModal";
import TJConversionPixel from "./components/TJConversionPixel";
import Layout from "./components/Layout";
import MediaProtection from "./components/MediaProtection";

// Bevarer query-string ved redirect (f.eks. /galleri?image=xx → /bilder?image=xx)
function RedirectWithQuery({ to }) {
  const loc = useLocation();
  return <Navigate to={`${to}${loc.search}`} replace />;
}

// Redirect der mål-URL allerede har query — fletter inn UTM-tagger med &
function RedirectMergeQuery({ to }) {
  const loc = useLocation();
  if (!loc.search) return <Navigate to={to} replace />;
  // loc.search inkluderer "?". Strip det og legg til som &
  const extra = loc.search.startsWith("?") ? loc.search.slice(1) : loc.search;
  const joiner = to.includes("?") ? "&" : "?";
  return <Navigate to={`${to}${joiner}${extra}`} replace />;
}

// 404-side med header/footer så bruker ikke møter helsvart skjerm ved død lenke
function NotFound() {
  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="not-found">
        <h1 className="font-fraunces italic text-4xl text-[var(--cream)] mb-3">404</h1>
        <p className="text-[var(--cream-soft)] mb-6">Siden finnes ikke.</p>
        <a href="/" className="btn-magenta inline-block">Til forsiden</a>
      </div>
    </Layout>
  );
}

import Home from "./pages/Home";
import BetaProfile from "./pages/BetaProfile";
import BetaSkaperSide from "./pages/BetaSkaperSide";
import Forum from "./pages/Forum";
import MineTrader from "./pages/MineTrader";
import ForumView from "./pages/ForumView";
import ThreadView from "./pages/ThreadView";
import Albums from "./pages/Albums";
import AlbumView from "./pages/AlbumView";
import Bilder from "./pages/Bilder";
import Noveller from "./pages/Noveller";
import NovelleView from "./pages/NovelleView";
import NovelleEditor from "./pages/NovelleEditor";
import Meldinger from "./pages/Meldinger";
import Sok from "./pages/Sok";
import Annonser from "./pages/Annonser";
import AnnonseListe from "./pages/AnnonseListe";
import AnnonseDetalj from "./pages/AnnonseDetalj";
import AnnonseEditor from "./pages/AnnonseEditor";
import Varsler from "./pages/Varsler";
import Mentions from "./pages/Mentions";
import Innstillinger from "./pages/Innstillinger";
import Groups from "./pages/Groups";
import GroupView from "./pages/GroupView";
import Skapere from "./pages/Skapere";
import Veyl from "./pages/Veyl";
import BlurSjekk from "./pages/BlurSjekk";
import EierReset from "./pages/EierReset";
import SkaperGalleri from "./pages/SkaperGalleri";
import BliSkaper from "./pages/BliSkaper";
import MinSkaperside from "./pages/MinSkaperside";
import MinInnsikt from "./pages/MinInnsikt";
import SkaperLykkehjul from "./pages/SkaperLykkehjul";
import SkaperStudio from "./pages/SkaperStudio";
import CreatorLinkStudio from "./pages/CreatorLinkStudio";
import { LinkLanding, LinkGate, LinkList } from "./pages/LinkPage";
import SkaperSosialAbonnenter from "./pages/SkaperSosialAbonnenter";
import SkaperAbonnementsplaner from "./pages/SkaperAbonnementsplaner";
import Chat from "./pages/Chat";
import GlimtStudio from "./pages/GlimtStudio";
import GenererDmca from "./pages/GenererDmca";
import GlimtViewer from "./pages/GlimtViewer";
import GlimtApp from "./pages/GlimtApp";
import GlimtSend from "./pages/GlimtSend";
import BankIdGate from "./components/BankIdGate";
import Samarbeid from "./pages/Samarbeid";
import SamarbeidSok from "./pages/SamarbeidSok";
import ExternalRequestDetail from "./pages/ExternalRequestDetail";
import OpenRequestDetail from "./pages/OpenRequestDetail";
import SamarbeidPakke from "./pages/SamarbeidPakke";
import SamarbeidEkstern from "./pages/SamarbeidEkstern";
import LykkehjulPage from "./pages/LykkehjulPage";
import LykkehjulVinst from "./pages/LykkehjulVinst";
import Konkurranser from "./pages/Konkurranser";
import KonkurranseView from "./pages/KonkurranseView";
import KonkurranseEnter from "./pages/KonkurranseEnter";
import AdminKonkurranseNy from "./pages/AdminKonkurranseNy";
import KonkurranseVinnere from "./pages/KonkurranseVinnere";
import Personvern from "./pages/Personvern";
import AdminModerasjon from "./pages/AdminModerasjon";
import Online from "./pages/Online";
import Search from "./pages/Search";
import Oppgrader from "./pages/Oppgrader";
import DesignPreview from "./pages/DesignPreview";
import Vip from "./pages/Vip";
import Wallet from "./pages/Wallet";
import Kjop from "./pages/Kjop";
import KjopBekreft from "./pages/KjopBekreft";
import KjopKryptoStatus from "./pages/KjopKryptoStatus";
import SelgerVerifisering from "./pages/SelgerVerifisering";
import SelgerLenker from "./pages/SelgerLenker";
import VerifiserBankID from "./pages/VerifiserBankID";
import Husregler from "./pages/Husregler";
import Kontakt from "./pages/Kontakt";
import BliOnlyfansSkaper from "./pages/BliOnlyfansSkaper";
import AdminStatistikk from "./pages/AdminStatistikk";
import AdminBoard from "./pages/admin/AdminBoard";
import AdminAktivitet from "./pages/admin/AdminAktivitet";
import AdminMediaDiagnose from "./pages/admin/AdminMediaDiagnose";
import AdminGamleAlbumFiler from "./pages/admin/AdminGamleAlbumFiler";
import AdminEpostLog from "./pages/admin/AdminEpostLog";
import AdminSkjema from "./pages/admin/AdminSkjema";
import AdminBrukere from "./pages/admin/AdminBrukere";
import AdminPromoInboks from "./pages/admin/AdminPromoInboks";
import Kvitteringer from "./pages/Kvitteringer";
import AdminSkjemaSvar from "./pages/admin/AdminSkjemaSvar";
import Skjema from "./pages/Skjema";
import AdminKampanjer from "./pages/admin/AdminKampanjer";
import SelgerHub from "./pages/SelgerHub";
import LeakSjekk from "./pages/LeakSjekk";
import LeakUpload from "./pages/LeakUpload";
import SocialSchedulerHub from "./pages/SocialSchedulerHub";
import SocialSchedulerList from "./pages/SocialSchedulerList";
import ParProfile from "./pages/ParProfile";
import ParInnstillinger from "./pages/ParInnstillinger";
import ParInnboks from "./pages/ParInnboks";
import AdminForum from "./pages/admin/AdminForum";
import VoteWheelAdmin from "./pages/admin/VoteWheelAdmin";
import AvstemningsHjul from "./pages/AvstemningsHjul";
import GlemtPassord from "./pages/GlemtPassord";
import TilbakestillPassord from "./pages/TilbakestillPassord";
import BekreftEpost from "./pages/BekreftEpost";
import AdminHelse from "./pages/AdminHelse";
import AdminOkonomi from "./pages/AdminOkonomi";
import OnboardingTour from "./components/OnboardingTour";
import BetaBanner from "./components/BetaBanner";
import usePageViewTracker from "./hooks/usePageViewTracker";
import CookieBanner from "./components/CookieBanner";
import PWAInstallBanner from "./components/PWAInstallBanner";
import SeoMeta from "./components/SeoMeta";
import SystemMessages from "./components/SystemMessages";
import EmailGateModal from "./components/EmailGateModal";
import CreatorExpiredModal from "./components/CreatorExpiredModal";
import { useAuth } from "./contexts/AuthContext";

function ConditionalGates() {
  const location = useLocation();
  const { user } = useAuth();
  usePageViewTracker();
  if (location.pathname.startsWith("/design")) return null;
  if (location.pathname.startsWith("/link/")) return null;
  return (
    <>
      <SystemMessages user={user} />
      <EmailGateModal />
      <CreatorExpiredModal />
      <OnboardingModal />
      <OnboardingTour />
      <BetaBanner />
      <TJConversionPixel />
    </>
  );
}

function GlobalOverlays() {
  const location = useLocation();
  if (location.pathname.startsWith("/link/")) return null;
  return (
    <>
      <CookieBanner />
      <PWAInstallBanner />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
        <BrowserRouter>
          <MediaProtection />
          <SeoMeta />
          <ConditionalGates />
          <GlobalOverlays />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/register" element={<RedirectMergeQuery to="/?tab=register" />} />
            <Route path="/registrer" element={<RedirectMergeQuery to="/?tab=register" />} />
            <Route path="/forum" element={<Forum />} />
            <Route path="/forum/mine" element={<MineTrader />} />
            <Route path="/forum/:forumId" element={<ForumView />} />
            <Route path="/thread/:threadId" element={<ThreadView />} />
            <Route path="/profile/:username" element={<BetaProfile />} />
            <Route path="/albums" element={<Albums />} />
            <Route path="/albums/:albumId" element={<AlbumView />} />
            <Route path="/bilder" element={<Bilder />} />
            <Route path="/noveller" element={<Noveller />} />
            <Route path="/sok" element={<Sok />} />
            <Route path="/annonser" element={<Annonser />} />
            <Route path="/annonser/ny" element={<AnnonseEditor />} />
            <Route path="/annonser/rediger/:id" element={<AnnonseEditor />} />
            <Route path="/annonser/se/:id" element={<AnnonseDetalj />} />
            <Route path="/annonser/:category" element={<AnnonseListe />} />
            <Route path="/annonser/:category/:subcategory" element={<AnnonseListe />} />
            <Route path="/noveller/ny" element={<NovelleEditor />} />
            <Route path="/noveller/:novelleId" element={<NovelleView />} />
            <Route path="/meldinger" element={<Meldinger />} />
            <Route path="/varsler" element={<Varsler />} />
            <Route path="/mentions" element={<Mentions />} />
            <Route path="/innstillinger" element={<Innstillinger />} />
            <Route path="/innstillinger/:section" element={<Innstillinger />} />
            <Route path="/grupper" element={<Groups />} />
            <Route path="/grupper/:groupId" element={<GroupView />} />
            {/* Backwards compat for old /groups paths */}
            <Route path="/groups" element={<Groups />} />
            <Route path="/groups/:groupId" element={<GroupView />} />
            <Route path="/skapere" element={<Skapere />} />
            <Route path="/veyl" element={<Veyl />} />
            <Route path="/sladd" element={<Veyl />} />
            <Route path="/sladd-sjekk" element={<BlurSjekk />} />
            <Route path="/er-sladdingen-trygg" element={<BlurSjekk />} />
            <Route path="/eier-reset" element={<EierReset />} />
            <Route path="/par/:handle" element={<ParProfile />} />
            <Route path="/innstillinger/par" element={<ParInnstillinger />} />
            <Route path="/par-innstillinger" element={<ParInnstillinger />} />
            <Route path="/par-innboks" element={<ParInnboks />} />
            <Route path="/skaper/:username/galleri" element={<SkaperGalleri />} />
            <Route path="/skaper/:username" element={<BetaSkaperSide />} />
            <Route path="/bli-skaper" element={<BliSkaper />} />
            <Route path="/bli-onlyfans-skaper" element={<BliOnlyfansSkaper />} />
            <Route path="/admin/statistikk" element={<AdminStatistikk />} />
            <Route path="/admin/board" element={<AdminBoard />} />
            <Route path="/admin/aktivitet" element={<AdminAktivitet />} />
            <Route path="/admin/media-diagnose" element={<AdminMediaDiagnose />} />
            <Route path="/admin/gamle-album-filer" element={<AdminGamleAlbumFiler />} />
            <Route path="/admin/epost-log" element={<AdminEpostLog />} />
            <Route path="/admin/skjema" element={<AdminSkjema />} />
            <Route path="/admin/brukere" element={<AdminBrukere />} />
            <Route path="/admin/promo-innboks" element={<AdminPromoInboks />} />
            <Route path="/kvitteringer" element={<Kvitteringer />} />
            <Route path="/admin/skjema/:formId" element={<AdminSkjemaSvar />} />
            <Route path="/skjema/:slug" element={<Skjema />} />
            <Route path="/admin/forum" element={<AdminForum />} />
            <Route path="/admin/kampanjer" element={<AdminKampanjer />} />
            <Route path="/admin/kampanjekoder" element={<AdminKampanjer />} />
            <Route path="/admin/referrals" element={<AdminKampanjer />} />
            <Route path="/admin/verving" element={<AdminKampanjer />} />
            <Route path="/admin/avstemnings-hjul" element={<VoteWheelAdmin />} />
            <Route path="/avstemnings-hjul/:contestId" element={<AvstemningsHjul />} />
            <Route path="/glemt-passord" element={<GlemtPassord />} />
            <Route path="/tilbakestill-passord/:token" element={<TilbakestillPassord />} />
            <Route path="/bekreft-epost/:token" element={<BekreftEpost />} />
            <Route path="/admin/helse" element={<AdminHelse />} />
            <Route path="/admin/okonomi" element={<AdminOkonomi />} />
            <Route path="/min-skaperside" element={<MinSkaperside />} />
            <Route path="/skaper/innsikt" element={<MinInnsikt />} />
            <Route path="/skaper/lykkehjul" element={<SkaperLykkehjul />} />
            <Route path="/skaper/studio" element={<SkaperStudio />} />
            <Route path="/skaper/studio/link-side" element={<CreatorLinkStudio />} />
            <Route path="/link/:slug" element={<LinkLanding />} />
            <Route path="/link/:slug/gate" element={<LinkGate />} />
            <Route path="/link/:slug/links" element={<LinkList />} />
            <Route path="/skaper/sosial-abonnenter" element={<SkaperSosialAbonnenter />} />
            <Route path="/skaper/sosial-abonnenter/planer" element={<SkaperAbonnementsplaner />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/skaper/studio/glimt" element={<BankIdGate nextUrl="/skaper/studio/glimt"><GlimtStudio /></BankIdGate>} />
            <Route path="/dmca" element={<GenererDmca />} />
            <Route path="/dmca-hjelp" element={<GenererDmca />} />
            <Route path="/glimt" element={<BankIdGate nextUrl="/glimt"><GlimtApp /></BankIdGate>} />
            <Route path="/glimt/send/:username" element={<BankIdGate nextUrl="/glimt"><GlimtSend /></BankIdGate>} />
            <Route path="/glimt/:username" element={<BankIdGate nextUrl="/glimt"><GlimtViewer /></BankIdGate>} />
            <Route path="/skaper/samarbeid" element={<Samarbeid />} />
            <Route path="/skaper/samarbeid/sok" element={<SamarbeidSok />} />
            <Route path="/skaper/samarbeid/henvendelse/:id" element={<ExternalRequestDetail />} />
            <Route path="/skaper/samarbeid/apen/:id" element={<OpenRequestDetail />} />
            <Route path="/skaper/samarbeid/pakker/ny" element={<SamarbeidPakke />} />
            <Route path="/skaper/samarbeid/pakker/:id/rediger" element={<SamarbeidPakke />} />
            <Route path="/samarbeid/invitasjon/:token" element={<SamarbeidEkstern />} />
            <Route path="/lykkehjul/vinst/:spinId" element={<LykkehjulVinst />} />
            <Route path="/lykkehjul/:slug" element={<LykkehjulPage />} />
            <Route path="/konkurranser" element={<Konkurranser />} />
            <Route path="/konkurranser/vinnere" element={<KonkurranseVinnere />} />
            <Route path="/konkurranser/:id" element={<KonkurranseView />} />
            <Route path="/konkurranser/:id/bidrag" element={<KonkurranseEnter />} />
            <Route path="/admin/konkurranser/ny" element={<AdminKonkurranseNy />} />
            <Route path="/personvern" element={<Personvern />} />
            <Route path="/vilkar" element={<Personvern />} />
            <Route path="/cookies" element={<Personvern />} />
            <Route path="/husregler" element={<Husregler />} />
            <Route path="/kontakt" element={<Kontakt />} />
            <Route path="/support" element={<Kontakt />} />
            <Route path="/hjelp" element={<Kontakt />} />
            <Route path="/vip" element={<Vip />} />
            <Route path="/wallet" element={<Wallet />} />
            <Route path="/kjop" element={<Kjop />} />
            <Route path="/kjop/bekreft/:purchaseId" element={<KjopBekreft />} />
            <Route path="/kjop/krypto-status/:purchaseId" element={<KjopKryptoStatus />} />
            <Route path="/innstillinger/selger-verifisering" element={<SelgerVerifisering />} />
            <Route path="/innstillinger/selger-lenker" element={<SelgerLenker />} />
            <Route path="/selger" element={<SelgerHub />} />
            <Route path="/leak-sjekk" element={<LeakSjekk />} />
            <Route path="/leak-sjekk/opplasting" element={<LeakUpload />} />
            <Route path="/admin/sosiale-medier" element={<SocialSchedulerHub />} />
            <Route path="/admin/sosiale-medier/:id" element={<SocialSchedulerList />} />
            <Route path="/verifiser/:token" element={<VerifiserBankID />} />
            <Route path="/admin/moderasjon" element={<AdminModerasjon />} />
            <Route path="/galleri" element={<RedirectWithQuery to="/bilder" />} />
            <Route path="/feed" element={<Navigate to="/skapere" replace />} />
            <Route path="/online" element={<Online />} />
            <Route path="/search" element={<Search />} />
            <Route path="/oppgrader" element={<Oppgrader />} />
            <Route path="/design" element={<DesignPreview />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Toaster />
        </BrowserRouter>
      </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
